import React, { useContext } from "react";
import styled from "styled-components";
import { useString as s } from "../../../../components/StringProvider";
import Currency from "../../../../components/Currency";
import { themeProp } from "../../../../theme";
import Heading from "../../../../components/Heading";
import Range from "../../../../components/Range";
import { useMobileMediaQuery } from "../../../../components/Responsive";
import OneTimeBenefits from "./OneTimeBenefits";
import KPIName from "./KPIName";
import WhiteDivider from "./WhiteDivider";
import CurrencyRange from "./CurrencyRange";
import ToText from "./ToText";
import {
  impactPercentageKPIsFilter,
  impactValueKPIsFilter,
  sortKPIsForValueHypothesis,
  strategicKPIsFilter
} from "../../../../utils/filters/kpi-filters";
import { connect } from "react-redux";
import { selectDiscoveryScenario } from "../../../../store/reducers";
import { compose } from "redux";
import { ValueHypothesisContext } from "../ValueHypothesisProvider";
import ViewValueHypothesisKPIsBy from "../../../../utils/view-value-hypothesis-kpis-by";

const Statistics = ({ kpis, currency, scenario }) => {
  const isMobile = useMobileMediaQuery();
  // Get sortBy from ValueHypothesisProvider
  const { sortBy } = useContext(ValueHypothesisContext);

  const strategicBenefitsText = s(
    "discovery.kpi.card.impact.strategicBenefits",
    "Strategic Benefits"
  );

  const impactValueKPIs = impactValueKPIsFilter(kpis);
  const impactPercentageKPIs = impactPercentageKPIsFilter(kpis);
  const strategicKPIs = strategicKPIsFilter(kpis);

  const totalLowImpactValue = impactValueKPIs.reduce(
    (sum, kpi) => sum + kpi.lowImpactValue,
    0
  );
  const totalHighImpactValue = impactValueKPIs.reduce(
    (sum, kpi) => sum + kpi.potentialImpactValue,
    0
  );

  // Sort impactValueKPIs by Value or Importance based on user selection
  sortKPIsForValueHypothesis({
    kpis: impactValueKPIs,
    viewValueHypothesisKPIsBy: sortBy,
    scenario
  });
  // impactPercentageKPIs have no $$$ value, so always sort by Importance
  sortKPIsForValueHypothesis({
    kpis: impactPercentageKPIs,
    viewValueHypothesisKPIsBy: ViewValueHypothesisKPIsBy.IMPORTANCE,
    scenario
  });
  // strategicKPIs have no $$$ value, so always sort by Importance
  sortKPIsForValueHypothesis({
    kpis: strategicKPIs,
    viewValueHypothesisKPIsBy: ViewValueHypothesisKPIsBy.IMPORTANCE,
    scenario
  });

  return (
    <Wrapper data-cy={"value-hypothesis-statistics"}>
      <Title>
        {s("discovery.opportunity.page.stats.header", "VALUE HYPOTHESIS")}
      </Title>
      <CurrencyRange>
        <Currency
          format="short"
          currency={currency}
          data-cy={"sidebar-element"}
        >
          {totalLowImpactValue}
        </Currency>
        {totalLowImpactValue !== totalHighImpactValue ? (
          <>
            <ToText />
            <Currency
              format="short"
              currency={currency}
              data-cy={"sidebar-element"}
            >
              {totalHighImpactValue}
            </Currency>
          </>
        ) : null}
      </CurrencyRange>
      {impactValueKPIs.map((kpi, index) => (
        <KPIName
          key={index}
          className={isMobile ? "mobile" : undefined}
          variant={isMobile ? "statisticsMobile" : "bMedium"}
          data-cy={"sidebar-element"}
        >
          {kpi.definition.name}
        </KPIName>
      ))}
      <OneTimeBenefits kpis={kpis} currency={currency} />
      {impactPercentageKPIs.length > 0 ? <WhiteDivider /> : null}
      {impactPercentageKPIs.map((kpi, index) => (
        <div
          key={index}
          style={{ paddingBottom: 16 }}
          data-cy={`sidebar-element-${kpi.kpiCode}`}
        >
          <PercentageRange data-cy={"sidebar-element"}>
            <Range
              first={kpi.lowImpactPercentage}
              second={kpi.highImpactPercentage}
              type={kpi.definition.impactFormat}
              customImpactFormat={kpi.definition.customImpactFormat}
              impactPercentageFormulaDescription={
                kpi.definition.impactPercentageFormulaDescription
              }
            />
          </PercentageRange>
          <KPIName
            key={index}
            className={isMobile ? "mobile" : undefined}
            variant={isMobile ? "statisticsMobile" : "bMedium"}
            data-cy={"sidebar-element"}
          >
            {kpi.definition.name}
          </KPIName>
        </div>
      ))}
      {strategicKPIs.length > 0 ? (
        <>
          <WhiteDivider />
          <Heading level={"h3"} color={"surface"}>
            {strategicBenefitsText}
          </Heading>{" "}
        </>
      ) : null}
      {strategicKPIs.map((kpi, index) => (
        <KPIName
          key={index}
          className={isMobile ? "mobile" : undefined}
          variant={isMobile ? "statisticsMobile" : "bMedium"}
          data-cy={"sidebar-element"}
        >
          {kpi.definition.name}
        </KPIName>
      ))}
    </Wrapper>
  );
};

const Title = styled(Heading).attrs(() => ({
  level: "h5"
}))`
  color: white;
  margin-bottom: 16px;
`;

const PercentageRange = styled(Heading).attrs(() => ({
  level: "h3"
}))`
  color: white;
  margin-bottom: 0;

  & .ant-typography {
    margin-bottom: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 4px rgba(5, 37, 67, 0.15);
  border-radius: 8px;
  padding: 24px;
  overflow-y: scroll;
  overflow-x: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;

  @media print {
    box-shadow: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  background-color: ${themeProp("palette.secondary")};
`;

const mapStateToProps = (state) => ({
  scenario: selectDiscoveryScenario(state)
});

export default compose(connect(mapStateToProps))(Statistics);
